import type { CustomVisitPagingVo, Page, ProductPagingParams, ProductStatisticAllParams } from "@zhihaoscm/entity";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps, type TableRequestData } from "zhihao-ui-react";

import { dashboardService } from "@/api/services/dashboardService";

export interface UserGoodsStatisticsForVisitorDialogProps {
    open: boolean;
    onClose: () => void;
    baseParams?: ProductStatisticAllParams & { productId?: string };
    loadRows?: (
        params: ProductPagingParams & { outerKeyword?: string; productId?: string }
    ) => Promise<Page<CustomVisitPagingVo>>;
}

type UserGoodsVisitorTableRow = CustomVisitPagingVo & { __tableRowKey?: string };

/** 为访客分页结果生成当前页内唯一的行键，避免无用户 ID 的游客行重复。 */
export const createUserGoodsVisitorTableRows = (
    list: CustomVisitPagingVo[],
    page: number,
    size: number
): UserGoodsVisitorTableRow[] =>
    list.map((row, index) => ({
        ...row,
        __tableRowKey: `user-goods-visitor-${page}-${size}-${index}`
    }));

/** 读取访客分页结果的稳定行键，兼容接口返回的真实用户标识。 */
export const getUserGoodsVisitorRowKey = (row: CustomVisitPagingVo) => {
    const tableRow = row as UserGoodsVisitorTableRow;
    return String(tableRow.__tableRowKey || row.customerId || row.customerCode || "visitor-row");
};

/** 用户商品访问明细默认接口，沿用旧 Vue custom-visit-paging 契约。 */
export async function loadUserGoodsVisitorStatistics(params: ProductPagingParams & { outerKeyword?: string }) {
    return dashboardService.customVisitPaging(params);
}

/** 单商品访问明细沿用旧 Vue 的访客单品分页接口。 */
export async function loadUserGoodsVisitorStatisticsByProductId(
    params: ProductPagingParams & { productId?: string; outerKeyword?: string }
) {
    if (!params.productId) {
        return { list: [], currentPage: Number(params.page), pageSize: Number(params.size), total: 0, totalPage: 0 };
    }
    const { outerKeyword, page, size, ...query } = params;
    return dashboardService.customVisitPagingByProductId({
        ...query,
        productId: params.productId,
        page: Number(page),
        size: Number(size),
        keyword: query.keyword || outerKeyword
    });
}

/** 组合外层商品筛选条件和访客明细分页条件，保持旧 Vue 请求字段命名。 */
export const buildUserGoodsVisitorStatisticsQuery = (
    rawParams: Record<string, unknown>,
    baseParams?: ProductStatisticAllParams & { productId?: string }
): ProductPagingParams & { outerKeyword?: string; productId?: string } => {
    const params = rawParams || {};
    const page = String(params.page ?? 1);
    const size = String(params.size ?? 10);
    const { keyword, ...outerParams } = baseParams || {};
    return {
        ...(outerParams as Omit<ProductPagingParams, "page" | "size">),
        ...(params as Omit<ProductPagingParams, "page" | "size">),
        page,
        size,
        scope: outerParams.scope === 6 ? undefined : outerParams.scope,
        ...(keyword ? { outerKeyword: keyword } : {})
    };
};

/** 访问用户明细表列，字段和搜索长度与旧 Vue 保持一致。 */
const createColumns = (navigate: ReturnType<typeof useNavigate>): TableColumnProps<CustomVisitPagingVo>[] => [
    {
        prop: "customerCode",
        label: "用户ID",
        minWidth: 200,
        search: {
            order: 1,
            el: "input",
            props: { placeholder: "请输入用户ID/手机号/个人实名/机构名称搜索", maxlength: 50 },
            key: "keyword"
        },
        render: ({ row }) => (
            <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => navigate(`/lianYunUser/userInfo?userId=${row.customerId ?? ""}`)}
            >
                {row.customerCode || "--"}
            </button>
        )
    },
    { prop: "realName", label: "个人实名", minWidth: 120 },
    { prop: "institutionName", label: "组织机构名称", minWidth: 250 },
    { prop: "mobile", label: "注册手机号", minWidth: 165 }
];

/** 用户商品访问明细弹窗，复用 zhihao-ui-react Table/Dialog 并保留 Vue 的 800 宽度。 */
export function UserGoodsStatisticsForVisitorDialog({
    open,
    onClose,
    baseParams,
    loadRows = loadUserGoodsVisitorStatistics
}: UserGoodsStatisticsForVisitorDialogProps) {
    const navigate = useNavigate();
    const columns = createColumns(navigate);
    const [sort, setSort] = useState<{ key: string; order: "asc" | "desc" }>();

    // 弹窗关闭后清除排序，下一次打开重新请求第一页数据。
    useEffect(() => {
        if (!open) setSort(undefined);
    }, [open]);

    const requestRows = useCallback(
        async (rawParams: unknown): Promise<TableRequestData<CustomVisitPagingVo>> => {
            const params = (rawParams || {}) as Record<string, unknown>;
            const query = buildUserGoodsVisitorStatisticsQuery(params, baseParams);
            if (sort) {
                query.sortKey = sort.key;
                query.sortOrder = sort.order;
            }
            const result = await loadRows(query);
            const page = query.page;
            const size = query.size;
            return {
                list: createUserGoodsVisitorTableRows(result.list || [], Number(page), Number(size)),
                currentPage: Number(page),
                pageSize: Number(size),
                total: result.total || 0
            };
        },
        [baseParams, loadRows, sort]
    );

    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order?: "ascending" | "descending" | null }) => {
            setSort(prop && order ? { key: prop, order: order === "ascending" ? "asc" : "desc" } : undefined);
        },
        []
    );

    return (
        <ZhDialog open={open} title="用户商品统计明细" width={800} onCancel={onClose} footer={null}>
            <ZhTable<CustomVisitPagingVo>
                key={`${open}-${sort?.key || ""}-${sort?.order || ""}`}
                requestApi={requestRows}
                columns={columns}
                rowKey={getUserGoodsVisitorRowKey}
                initParam={{ page: 1, size: 10 }}
                requestAuto={open}
                onSortChange={handleSortChange}
            />
        </ZhDialog>
    );
}

export default UserGoodsStatisticsForVisitorDialog;
